import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ScrollText } from "lucide-react";

const LAST_UPDATED = "14 February 2026";

export default function TermsPage() {
  return (
    <div data-testid="page-terms" className="max-w-3xl mx-auto">
      <div className="mb-6 border-b border-[color:var(--sat-border)] pb-4">
        <Link to="/news" className="sat-label inline-flex items-center gap-2 mb-2 hover:sat-cyan" data-testid="terms-back">
          <ArrowLeft size={12} /> BACK
        </Link>
        <div className="sat-label sat-cyan flex items-center gap-2">
          <ScrollText size={12} /> LEGAL // TERMS &amp; CONDITIONS
        </div>
        <h1 className="font-display font-bold text-3xl md:text-4xl mt-1">Terms &amp; Conditions</h1>
        <p className="sat-label mt-2 text-[color:var(--sat-text-dim)]">Last updated · {LAST_UPDATED}</p>
      </div>

      <article className="prose prose-invert max-w-none sat-body text-sm leading-relaxed space-y-6" data-testid="terms-content">

        <section>
          <h2 className="font-display font-bold text-lg sat-cyan">1. Acceptance of Terms</h2>
          <p>
            ASTRONATLAS ("we", "us", "our") provides a space-tracking service that aggregates public
            data feeds from The Space Devs Launch Library, the Spaceflight News API, Wikipedia, and other
            open data sources ("Service"). By registering an account, uploading content, or otherwise
            using the Service, you ("you", "user", "pilot") agree to be bound by these Terms &amp;
            Conditions ("Terms"). If you do not agree, do not use the Service.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-lg sat-cyan">2. Governing Law &amp; Jurisdiction</h2>
          <p>
            These Terms are governed by and construed in accordance with the laws of the
            Republic of India, including the Information Technology Act, 2000 (as amended),
            the Information Technology (Intermediary Guidelines and Digital Media Ethics Code)
            Rules, 2021, and the Digital Personal Data Protection Act, 2023 ("DPDP Act"). Any
            dispute arising under or in connection with these Terms shall be subject to the
            exclusive jurisdiction of the competent courts located in New Delhi, India.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-lg sat-cyan">3. User Accounts &amp; Privacy</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>You are responsible for keeping your credentials confidential.</li>
            <li>
              We collect only the personal data you provide during registration (email, chosen
              callsign) and content you voluntarily submit (messages, uploads, favorites). Your email
              is <strong>never displayed publicly</strong>; other pilots see only your callsign.
            </li>
            <li>
              We process personal data as a Data Fiduciary under the DPDP Act, 2023, for the
              limited purposes of providing the Service, moderation, and communicating essential
              service notices. Consent may be withdrawn by deleting your account
              (contact the owner-admin below).
            </li>
            <li>You may request access to, correction of, or deletion of your personal data at any time.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display font-bold text-lg sat-cyan">4. Community Content &amp; Uploads</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>You retain ownership of the content you post, but grant us a non-exclusive,
              worldwide, royalty-free license to store, display, and transmit it as required to
              operate the Service.</li>
            <li>You must not upload material that is unlawful, defamatory, infringing, obscene,
              or that violates Rule 3(1)(b) of the IT (Intermediary Guidelines) Rules, 2021.</li>
            <li>Individual files may not exceed 25 MB. We may remove any content that violates
              these Terms or applicable Indian law without prior notice.</li>
            <li>The owner-admin is automatically a moderator of every community group and may
              archive, freeze, or close a group at any time to preserve platform integrity.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display font-bold text-lg sat-cyan">5. Prohibited Conduct</h2>
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Impersonate any person, agency, or spacecraft.</li>
            <li>Harass, abuse, or threaten any other user.</li>
            <li>Attempt to reverse-engineer, scrape at abusive rates, or otherwise disrupt the Service.</li>
            <li>Use the Service to promote hate speech, terrorism, sexually explicit content
              involving minors, or content prohibited under Sections 66A / 67 / 67A / 67B of the IT Act, 2000.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display font-bold text-lg sat-cyan">6. Third-Party Data</h2>
          <p>
            Launch, rocket, and news data is provided by third parties (The Space Devs,
            Spaceflight News API, Wikipedia). We do not warrant its accuracy, timeliness, or
            fitness for any purpose. Do not rely on ASTRONATLAS for operational or safety-critical
            decisions.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-lg sat-cyan">7. Intermediary Liability</h2>
          <p>
            ASTRONATLAS acts as an intermediary within the meaning of Section 2(1)(w) of the IT Act,
            2000, and complies with the safe-harbour requirements of Section 79 read with the
            Intermediary Guidelines, 2021. We are not liable for user-generated content that we
            did not initiate, select, or modify. Grievances may be raised as per Section 11 below.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-lg sat-cyan">8. Disclaimer &amp; Limitation of Liability</h2>
          <p>
            The Service is provided on an "as is" and "as available" basis without warranties of
            any kind, either express or implied. To the maximum extent permitted by Indian law, in
            no event shall ASTRONATLAS, its founders, or its contributors be liable for any
            indirect, incidental, punitive, or consequential damages arising out of the use of, or
            inability to use, the Service. Total aggregate liability, if any, shall not exceed
            INR 1,000.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-lg sat-cyan">9. Termination</h2>
          <p>
            We may suspend or terminate your account at any time for violation of these Terms
            or applicable law. You may terminate your account at any time by contacting the
            owner-admin. Content you posted prior to termination may remain available in
            aggregated or anonymised form as permitted by law.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-lg sat-cyan">9A. Account Recovery Notice</h2>
          <p>
            Changing the password without being logged in is currently not a feature that works,
            will be fixed in future updates. If you forget your password while logged out, please
            contact the owner-admin at the grievance address below and we will reset it manually.
            Pilots who are logged in can change their access code at any time from the
            <span className="sat-cyan"> PILOT PROFILE</span> screen — the current session serves as
            proof of identity, so no prior password is required.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-lg sat-cyan">10. Changes to these Terms</h2>
          <p>
            We may revise these Terms from time to time. Continued use of the Service after any
            revision constitutes acceptance of the updated Terms. Material changes will be
            announced within the Service.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-lg sat-cyan">11. Grievance Officer</h2>
          <p>
            In compliance with Rule 3(2) of the IT (Intermediary Guidelines) Rules, 2021 and
            Section 10 of the DPDP Act, 2023, complaints regarding content or personal-data
            processing may be addressed to the Grievance Officer:
          </p>
          <ul className="list-none pl-0 space-y-0.5 font-mono text-xs mt-2">
            <li><strong>Grievance Officer</strong>: ASTRONATLAS Ops</li>
            <li><strong>Email</strong>: sgexecutioner@gmail.com</li>
            <li><strong>Response window</strong>: within 15 (fifteen) days of receipt</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display font-bold text-lg sat-cyan">12. Contact</h2>
          <p>
            For general questions about these Terms, reach out via the same email above.
          </p>
        </section>

        <p className="sat-label text-[color:var(--sat-text-dim)] pt-4 border-t border-[color:var(--sat-border)] mt-8">
          By using ASTRONATLAS, you acknowledge that you have read, understood, and agreed to
          be bound by these Terms.
        </p>
      </article>
    </div>
  );
}
